import { useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmModalV2 } from '@components/modals/confirm-modal-v2';
import { PillToggleVariant } from '@components/pill-toggle';
import { PillToggleGroupOption } from '@components/pill-toggle-group';
import classNames from 'classnames';

import { FormFieldRowDesktop } from './components/form-field-row-desktop';
import { FormFieldRowEditor } from './components/form-field-row-editor';
import { FormFieldRowLocked } from './components/form-field-row-locked';
import { FormFieldRowMobile } from './components/form-field-row-mobile';
import { FormFieldRowProps, FormFieldRowRequirement, FormFieldRowRequirementLabels } from './form-field-row.type';
import { EMPTY_FORM_FIELD_ROW_ERRORS, getFormFieldRowErrors, hasFormFieldRowErrors } from './validation';

const REQUIREMENT_VARIANT: Record<FormFieldRowRequirement, PillToggleVariant> = {
  off: 'critical',
  optional: 'warning',
  required: 'success',
};

const DEFAULT_REQUIREMENT_LABELS: Required<FormFieldRowRequirementLabels> = {
  off: 'Off',
  optional: 'Optional',
  required: 'Required',
};

export const FormFieldRow = ({
  label,
  value,
  onSave,
  onDelete,
  requirement,
  onRequirementChange,
  onMobileEditClick,
  locked = false,
  disabled = false,
  isCustom = true,
  isNew = false,
  defaultExpanded = false,
  onExpandedChange,
  deleteConfirm,
  modalZIndex,
  modalSurfaceClassName,
  editAriaLabel = 'Edit',
  deleteAriaLabel = 'Delete',
  mobileEditAriaLabel,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  availableTypes,
  requirementLabels,
  typeLabels,
  namePlaceholder,
  minPlaceholder,
  maxPlaceholder,
  className,
}: FormFieldRowProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [draft, setDraft] = useState(value);
  const [errors, setErrors] = useState(EMPTY_FORM_FIELD_ROW_ERRORS);
  const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  const resolvedRequirementLabels: Required<FormFieldRowRequirementLabels> = {
    off: requirementLabels?.off ?? DEFAULT_REQUIREMENT_LABELS.off,
    optional: requirementLabels?.optional ?? DEFAULT_REQUIREMENT_LABELS.optional,
    required: requirementLabels?.required ?? DEFAULT_REQUIREMENT_LABELS.required,
  };

  const requirementOptions = useMemo<PillToggleGroupOption<FormFieldRowRequirement>[]>(
    () => [
      { value: 'off', label: resolvedRequirementLabels.off, variant: REQUIREMENT_VARIANT.off },
      { value: 'optional', label: resolvedRequirementLabels.optional, variant: REQUIREMENT_VARIANT.optional },
      { value: 'required', label: resolvedRequirementLabels.required, variant: REQUIREMENT_VARIANT.required },
    ],
    [resolvedRequirementLabels.off, resolvedRequirementLabels.optional, resolvedRequirementLabels.required]
  );

  const requirementVariant = REQUIREMENT_VARIANT[requirement];
  const requirementLabel = resolvedRequirementLabels[requirement];

  const expand = () => {
    setIsExpanded(true);
    onExpandedChange?.(true);
  };

  const collapse = () => {
    setIsExpanded(false);
    onExpandedChange?.(false);
  };

  const handleEditClick = () => {
    if (locked || disabled) return;
    setDraft(value);
    setErrors(EMPTY_FORM_FIELD_ROW_ERRORS);
    expand();
  };

  const handleDraftChange = (next: typeof draft) => {
    setDraft(next);
    setErrors(EMPTY_FORM_FIELD_ROW_ERRORS);
  };

  const handleSave = () => {
    const nextErrors = getFormFieldRowErrors(draft);
    if (hasFormFieldRowErrors(nextErrors)) {
      setErrors(nextErrors);
      return;
    }
    setErrors(EMPTY_FORM_FIELD_ROW_ERRORS);
    onSave(draft);
    collapse();
  };

  useEffect(() => {
    if (!isExpanded) return;
    const handler = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (!rowRef.current?.contains(document.activeElement)) return;
      setIsExpanded(false);
      onExpandedChange?.(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isExpanded, onExpandedChange]);

  const handleCancel = () => {
    if (isNew && onDelete) {
      setIsDeleteConfirming(true);
      return;
    }
    collapse();
  };

  const handleDeleteConfirm = () => {
    onDelete?.();
    setIsDeleteConfirming(false);
    collapse();
  };

  const mobileAriaLabel = mobileEditAriaLabel ?? `Edit ${typeof label === 'string' ? label : 'field'}`;

  return (
    <div
      ref={rowRef}
      className={classNames('flex w-full flex-col', { 'bg-secondary': isExpanded }, className)}
      data-testid='form-field-row'>
      <div
        className={classNames('flex w-full items-center', {
          'h-[46px]': !isExpanded,
          'h-10': isExpanded,
        })}>
        {locked ? (
          <FormFieldRowLocked
            label={label}
            requirementVariant={requirementVariant}
            requirementLabel={requirementLabel}
          />
        ) : (
          <>
            <FormFieldRowDesktop
              label={label}
              isCustom={isCustom}
              isExpanded={isExpanded}
              onEditClick={handleEditClick}
              onDeleteClick={() => setIsDeleteConfirming(true)}
              hasDelete={Boolean(onDelete)}
              requirement={requirement}
              onRequirementChange={onRequirementChange}
              requirementOptions={requirementOptions}
              disabled={disabled}
              editAriaLabel={editAriaLabel}
              deleteAriaLabel={deleteAriaLabel}
            />
            <FormFieldRowMobile
              label={label}
              isCustom={isCustom}
              requirementVariant={requirementVariant}
              requirementLabel={requirementLabel}
              onClick={onMobileEditClick}
              disabled={disabled}
              ariaLabel={mobileAriaLabel}
            />
          </>
        )}
      </div>

      {isExpanded && (
        <div className='hidden w-full flex-col xs:flex'>
          <FormFieldRowEditor
            value={draft}
            onChange={handleDraftChange}
            onCancel={handleCancel}
            onSave={handleSave}
            saveLabel={saveLabel}
            cancelLabel={cancelLabel}
            availableTypes={availableTypes}
            typeLabels={typeLabels}
            namePlaceholder={namePlaceholder}
            minPlaceholder={minPlaceholder}
            maxPlaceholder={maxPlaceholder}
            nameError={errors.name}
            minError={errors.min}
            maxError={errors.max}
          />
        </div>
      )}

      {onDelete && (
        <ConfirmModalV2
          isOpen={isDeleteConfirming}
          type='critical'
          title={deleteConfirm?.title ?? 'Delete row?'}
          description={deleteConfirm?.description}
          confirmText={deleteConfirm?.confirmText ?? 'Delete'}
          cancelText={deleteConfirm?.cancelText ?? 'Cancel'}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setIsDeleteConfirming(false)}
          surfaceClassName={modalSurfaceClassName}
          zIndex={modalZIndex}
        />
      )}
    </div>
  );
};
